import { useState, useCallback } from 'react';
import { sportTaxonomiesEndpoints } from '../../../endpoints/trainingStatistics';
import type { IClassificationSuggestion } from '../../../models/trainingStatistics';

/**
 * Hook: chiama endpoint suggest-classification e ritorna suggerimento + stato.
 * Spec: LEOPART_ProgrammazioneCampo_Spec_v1.1.1.md righe 2577-2615.
 */
interface BlockData {
  exerciseName?: string;
  tags?: string[];
  notes?: string;
  order?: number;
  durationMinutes?: number;
}

interface UseSuggestState {
  suggestion: IClassificationSuggestion | null;
  isLoading: boolean;
  error: string | null;
  fetchSuggestion: () => Promise<void>;
  clearSuggestion: () => void;
  sendFeedback: (accepted: boolean, chosenPhaseId?: string, chosenMethodId?: string) => Promise<void>;
}

export function useBlockClassificationSuggest(
  blockData: BlockData,
  sport: string,
  totalBlocks?: number,
): UseSuggestState {
  const [suggestion, setSuggestion] = useState<IClassificationSuggestion | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSuggestion = useCallback(async () => {
    if (!sport) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await sportTaxonomiesEndpoints.suggestClassification({
        exerciseName: blockData.exerciseName,
        tags: blockData.tags,
        notes: blockData.notes,
        sportType: sport,
        blockOrder: blockData.order,
        totalBlocks,
        durationMinutes: blockData.durationMinutes,
      });
      setSuggestion(result);
    } catch {
      setError('Impossibile ottenere suggerimenti classificazione.');
    } finally {
      setIsLoading(false);
    }
  }, [blockData.exerciseName, blockData.tags, blockData.notes, blockData.order, blockData.durationMinutes, sport, totalBlocks]);

  const clearSuggestion = useCallback(() => {
    setSuggestion(null);
    setError(null);
  }, []);

  const sendFeedback = useCallback(
    async (accepted: boolean, chosenPhaseId?: string, chosenMethodId?: string) => {
      if (!suggestion || !sport) return;
      try {
        await sportTaxonomiesEndpoints.sendFeedback({
          sport,
          suggestedSessionPhaseId: suggestion.sessionPhaseId ?? undefined,
          suggestedDidacticMethodId: suggestion.didacticMethodId ?? undefined,
          chosenSessionPhaseId: chosenPhaseId,
          chosenDidacticMethodId: chosenMethodId,
          accepted,
        });
      } catch {
        // feedback non bloccante — ignora silenziosamente
      }
    },
    [suggestion, sport],
  );

  return { suggestion, isLoading, error, fetchSuggestion, clearSuggestion, sendFeedback };
}
